import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';

export type PricingUpdateRoleKind = 'authorised' | 'approver';

export type PricingUpdateRoleRow = {
  id: string;
  email: string;
  role: PricingUpdateRoleKind;
  created_at: string;
  created_by: string | null;
};

const TABLE = 'pricing_update_roles';

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export async function fetchPricingUpdateRoles(): Promise<PricingUpdateRoleRow[]> {
  const db = getDbSupabase('forms');
  const { data, error } = await db
    .from(TABLE)
    .select('id,email,role,created_at,created_by')
    .order('role', { ascending: true })
    .order('email', { ascending: true });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => ({
    id: String(row.id),
    email: normalizeEmail(String(row.email ?? '')),
    role: String(row.role) as PricingUpdateRoleKind,
    created_at: String(row.created_at ?? ''),
    created_by: row.created_by == null ? null : String(row.created_by),
  }));
}

export async function addPricingUpdateRole(
  email: string,
  role: PricingUpdateRoleKind,
  createdBy?: string | null,
): Promise<void> {
  const normalized = normalizeEmail(email);
  if (!normalized || !normalized.includes('@')) {
    throw new Error('Enter a valid email address.');
  }
  const db = getDbSupabase('forms');
  const { error } = await db.from(TABLE).upsert(
    {
      email: normalized,
      role,
      created_by: createdBy?.trim() || null,
    },
    { onConflict: 'email,role' },
  );
  if (error) throw new Error(formatSupabaseError(error));
}

export async function removePricingUpdateRole(id: string): Promise<void> {
  const db = getDbSupabase('forms');
  const { error } = await db.from(TABLE).delete().eq('id', id);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function fetchPricingUpdateRolesForEmail(
  email: string,
): Promise<PricingUpdateRoleKind[]> {
  const normalized = normalizeEmail(email);
  if (!normalized) return [];
  const db = getDbSupabase('forms');
  const { data, error } = await db.from(TABLE).select('role').eq('email', normalized);
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? [])
    .map((row) => String(row.role) as PricingUpdateRoleKind)
    .filter((role) => role === 'authorised' || role === 'approver');
}

export async function setPricingUpdateRoleEnabled(
  email: string,
  role: PricingUpdateRoleKind,
  enabled: boolean,
  createdBy?: string | null,
): Promise<void> {
  if (enabled) {
    await addPricingUpdateRole(email, role, createdBy);
    return;
  }
  const normalized = normalizeEmail(email);
  if (!normalized) return;
  const db = getDbSupabase('forms');
  const { error } = await db.from(TABLE).delete().eq('email', normalized).eq('role', role);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function fetchMyPricingUpdateCapabilities(actorEmail: string | null | undefined): Promise<{
  isAuthorised: boolean;
  isApprover: boolean;
}> {
  const email = normalizeEmail(actorEmail ?? '');
  if (!email) return { isAuthorised: false, isApprover: false };

  const db = getDbSupabase('forms');
  const [{ data: authData }, { data: apprData }] = await Promise.all([
    db.rpc('af_puf_is_authorised'),
    db.rpc('af_puf_is_approver'),
  ]);

  // Prefer RPCs (includes IT/Management department elevated). Fall back to table read.
  if (typeof authData === 'boolean' || typeof apprData === 'boolean') {
    return {
      isAuthorised: Boolean(authData),
      isApprover: Boolean(apprData),
    };
  }

  const { data, error } = await db.from(TABLE).select('role').eq('email', email);
  if (error) throw new Error(formatSupabaseError(error));
  const roles = new Set((data ?? []).map((row) => String(row.role)));
  return {
    isAuthorised: roles.has('authorised'),
    isApprover: roles.has('approver'),
  };
}
